/**
 * Point entity → `RenderableEntity` adapter.
 *
 * Reads `Position X / Y / Z` and the active PDMODE / PDSIZE settings.
 * The PDMODE bitset and PDSIZE semantics are baked into the geometry
 * so the renderer (PR-B `pointMarkerMeshes.ts`) can pick the right
 * marker prototype without re-parsing.
 */

import type { EntityPreview } from "@dxf-ai-editor/contracts";

import { readNumber } from "../../scene/renderHelpers";
import type { RenderableEntity, Vec3 } from "../types";

import {
  buildRenderableShell,
  type AdapterContext,
} from "./adapterContext";
import { buildPointSceneGeometry } from "./sceneGeometryBuilders";

/** Adapter for DXF POINT / `Point` entity type. */
export function pointToRenderable(
  entity: EntityPreview,
  ctx: AdapterContext & { pdmode?: number; pdsize?: number },
): RenderableEntity {
  const positionRaw: Vec3 = {
    x: readNumber(entity.properties, "Position X"),
    y: readNumber(entity.properties, "Position Y"),
    z: readNumber(entity.properties, "Position Z"),
  };

  // PDMODE / PDSIZE defaults are resolved inside the shared builder.
  const { geometry, bounds } = buildPointSceneGeometry(
    positionRaw,
    ctx.pdmode,
    ctx.pdsize,
  );

  return buildRenderableShell(entity, ctx, geometry, bounds);
}
